import { FC, useState } from 'react';
import { useTranslation } from 'next-i18next';
import styled from 'styled-components';

import { Button, media } from '@/shared/components';
import { COLORS } from '@/shared/constants/colors';
import { useIsDesktop } from '@/shared/hooks/use-is-desktop';
import { PersonalIdentifierType } from '@/shared/lib/sj-orm/models/document/personal-identifier.document.dto';
import { Icon, IconName } from '@/shared/uikit/Icon';

import { InterwillDocumentFormType } from './types';

type InterwillSelectDocumentTypeFormProps = {
  onConfirm: (type: InterwillDocumentFormType) => void;
  setDocumentFormType: (document: InterwillDocumentFormType) => void;
};

export const InterwillSelectDocumentTypeForm: FC<InterwillSelectDocumentTypeFormProps> = ({
  onConfirm,
  setDocumentFormType,
}) => {
  const isDesktop = useIsDesktop();

  const { t } = useTranslation('documents');
  const [selectedType, setSelectedType] = useState<InterwillDocumentFormType>();

  const documents: {
    type: InterwillDocumentFormType;
    iconName: IconName;
    title: string;
  }[] = [
    {
      type: PersonalIdentifierType.PASSPORT,
      iconName: 'PersonalIdPassport',
      title: t('enums.docCategory.passport'),
    },
    {
      type: PersonalIdentifierType.NATIONAL_ID,
      iconName: 'PersonalIdNationalId',
      title: t('enums.docCategory.nationalId'),
    },
  ];

  return (
    <>
      <ItemsContainer>
        {documents.map((item) => {
          const isSelected = selectedType === item.type;
          return (
            <ItemContainer
              onClick={() => setSelectedType(item.type)}
              selected={isSelected}
              key={item.type}
            >
              <ItemLeftSide>
                <Icon name={item.iconName} />
                {item.title}
              </ItemLeftSide>

              {isSelected && <Icon name="check" style={{ color: COLORS.blueColor }} />}
            </ItemContainer>
          );
        })}
      </ItemsContainer>

      <ButtonContainer>
        <Button
          text="Next"
          isBlock={!isDesktop}
          disabled={!selectedType}
          onClick={() => {
            if (!selectedType) {
              return;
            }

            onConfirm(selectedType);
            setDocumentFormType(selectedType);
          }}
        />
      </ButtonContainer>
    </>
  );
};

const ItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 24px;
  ${media.mobile} {
    padding: 16px;
    padding-top: 8px;
  }
`;

const ItemContainer = styled.div<{ selected: boolean }>`
  display: flex;
  padding: 10px 12px;
  gap: 8px;
  align-items: center;
  border-radius: 8px;
  justify-content: space-between;

  background-color: ${(p) => (p.selected ? COLORS.colorSelectActiveOption : '')};

  &:hover {
    background-color: ${(p) => (p.selected ? COLORS.colorSelectActiveOption : COLORS.light300)};
  }
  cursor: pointer;
`;

const ItemLeftSide = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  padding: 16px 24px;
  justify-content: flex-end;

  ${media.mobile} {
    padding: 16px;
  }
`;
